import SharedProcessingTimeSection, { type ProcessingOption } from "@/components/feature/SharedProcessingTimeSection";

const options: ProcessingOption[] = [
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission — no drive to Miami needed",
      "Ideal for SRQ airport international travelers",
      "Perfect for Ringling College students studying abroad",
      "Great for seasonal residents and snowbirds",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service at Sarasota post offices",
      "Good for planned international travel",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only",
      "No document review — errors cause rejection",
      "In-person clerk appointment required at local facilities",
      "Workload shifts with government shutdowns and budget fights",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Cannot upgrade speed after submission",
    ],
    highlight: false,
    muted: true,
  },
  {
    speed: "24",
    unit: "Hours*",
    label: "Regional Agency Processing",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Closest regional agency: Miami — no Gulf Coast walk-in",
      "NPIC appointment required before traveling to Miami",
      "Life-or-death emergencies only",
      "Bereavement or medical crisis documentation required",
      "Expect to wait all day or return next day",
    ],
    highlight: false,
    emergency: true,
  },
];

export default function SarasotaProcessingTimeSection() {
  const timeframeStrip = (
    <div className="mt-8 bg-[#F7F8FA] border border-gray-200 rounded-lg px-5 py-4">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#1A365D] mb-3">Common Sarasota Timelines</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { speed: "2–3 Business Days", desc: "Urgent travel, last-minute arts tours" },
          { speed: "5–7 Business Days", desc: "Most popular for Cultural Coast travelers" },
          { speed: "1–2 Weeks", desc: "Ringling study abroad, seasonal residents" },
          { speed: "3–5 Weeks", desc: "Planned international travel" },
        ].map((item) => (
          <a
            key={item.speed}
            href="https://fastpassportcenter.com/checkout/"
            className="block text-center p-3 rounded-md hover:bg-white transition-colors"
          >
            <p className="text-sm font-bold text-[#1A365D] mb-1">{item.speed}</p>
            <p className="text-xs text-gray-500 leading-snug">{item.desc}</p>
          </a>
        ))}
      </div>
    </div>
  );

  return (
    <SharedProcessingTimeSection
      cityName="Sarasota"
      introText="Sarasota travelers — whether flying to Europe for a gallery tour, cruising the Caribbean from Tampa, or sending a Ringling student abroad — have several options. Here is how they compare, and why most cultural travelers and beach community residents choose a U.S. Department of State registered passport courier for dependable, predictable service without the 3.5-hour drive to Miami."
      options={options}
      emergencyDescription="Sarasota has no regional passport agency. Closest is Miami ~230 mi via I-75 (3.5+ hrs). Florida's single agency in Miami serves 22M+ residents statewide — appointments vanish weeks ahead during peak travel seasons. I-75 construction through Fort Myers and unpredictable Alligator Alley closures can extend the drive significantly. Hurricane evacuation traffic June–Nov clogs I-75 in both directions simultaneously."
      disclaimerText="*Processing speed depends on eligibility and documentation. Sarasota does not have a regional passport agency — the closest is Miami. Not all applicants qualify for Regional Agency Processing."
      afterCardsContent={timeframeStrip}
    />
  );
}